import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Handshake, Plus, Search, Loader2, Users, DollarSign, TrendingUp,
  Edit2, Power, PowerOff, Copy, ExternalLink, X, Check, Mail, AlertTriangle, QrCode, Download, Info, Pencil, BookOpen, LayoutDashboard
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { IconLegend } from '@/components/IconLegend';

const PARTNER_LEGEND = [
  { icon: <Pencil className="w-3.5 h-3.5" />, cls: 'bg-blue-50 text-blue-600', label: 'Editar dados do parceiro' },
  { icon: <DollarSign className="w-3.5 h-3.5" />, cls: 'bg-emerald-50 text-emerald-600', label: 'Registrar lançamento (crédito/pagamento)' },
  { icon: <QrCode className="w-3.5 h-3.5" />, cls: 'bg-[#C5A059]/20 text-[#C5A059]', label: 'Ver QR Code + Link do parceiro' },
  { icon: <Mail className="w-3.5 h-3.5" />, cls: 'bg-blue-50 text-blue-500', label: 'Enviar relatório mensal por e-mail' },
  { icon: <Copy className="w-3.5 h-3.5" />, cls: 'bg-slate-100 text-slate-500', label: 'Copiar link de indicação' },
  { icon: <ExternalLink className="w-3.5 h-3.5" />, cls: 'bg-slate-100 text-slate-500', label: 'Abrir landing page do parceiro' },
  { icon: <LayoutDashboard className="w-3.5 h-3.5" />, cls: 'bg-[#C5A059]/20 text-[#C5A059]', label: 'Acessar o painel do parceiro' },
  { icon: <BookOpen className="w-3.5 h-3.5" />, cls: 'bg-amber-50 text-amber-600', label: 'Ver Guia do Parceiro' },
  { icon: <Power className="w-3.5 h-3.5" />, cls: 'bg-red-50 text-red-500', label: 'Ativar / desativar parceiro' },
];
import { QRCodeCanvas } from 'qrcode.react';
import { API } from './adminConstants';
import { PartnerGuide } from '@/components/PartnerGuide';

const INITIAL_FORM = {
  name: '', email: '', cnpj: '', company_type: 'MEI', phone: '', commission_rate: '20', slug: '', wallet_id: '',
  postal_code: '', address: '', address_number: '', province: '', complement: '', skip_asaas: false
};

const AdminPartnersTab = () => {
  const { getToken } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingRate, setEditingRate] = useState(null);
  const [editRateValue, setEditRateValue] = useState('');
  const [sendingReport, setSendingReport] = useState(null);
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [editingWallet, setEditingWallet] = useState(null);
  const [walletInput, setWalletInput] = useState('');
  const [showQR, setShowQR] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPayment, setShowPayment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDesc, setPaymentDesc] = useState('');
  const [paymentType, setPaymentType] = useState('debit');
  const [savingPayment, setSavingPayment] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [guidePartner, setGuidePartner] = useState(null); // null = generico

  const generateSlug = (text) => {
    return text
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/[\s]+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleNameChange = (value) => {
    const slug = generateSlug(value);
    setForm(f => ({ ...f, name: value, slug }));
  };

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Erro ao carregar parceiros');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.cnpj.trim()) {
      toast.error('Preencha Nome, Email e CNPJ');
      return;
    }
    if (!form.slug.trim()) {
      toast.error('O slug do link não pode ficar vazio');
      return;
    }
    if (!form.wallet_id.trim() && !form.skip_asaas && (!form.postal_code.trim() || !form.address.trim() || !form.address_number.trim() || !form.province.trim())) {
      toast.error('Para criar subconta Asaas, preencha CEP, Rua, Número e Bairro. Ou marque "Cadastrar sem Asaas".');
      return;
    }
    setCreating(true);
    try {
      const token = getToken();
      await axios.post(`${API}/admin/partners`, {
        ...form,
        slug: form.slug.trim(),
        commission_rate: parseFloat(form.commission_rate) || 20
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Parceiro criado com sucesso!');
      setShowForm(false);
      setForm({ ...INITIAL_FORM });
      fetchPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar parceiro');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (partner) => {
    try {
      const token = getToken();
      const newStatus = partner.status === 'active' ? 'inactive' : 'active';
      await axios.put(`${API}/admin/partners/${partner.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Parceiro ${newStatus === 'active' ? 'ativado' : 'desativado'}`);
      fetchPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const handleSaveRate = async (partnerId) => {
    const rate = parseFloat(editRateValue);
    if (isNaN(rate) || rate < 1 || rate > 90) {
      toast.error('Taxa deve ser entre 1% e 90%');
      return;
    }
    try {
      const token = getToken();
      await axios.put(`${API}/admin/partners/${partnerId}`, { commission_rate: rate }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Comissão atualizada para ${rate}%`);
      setEditingRate(null);
      fetchPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/parceiro/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado!');
  };

  const handleSendReport = async (partner) => {
    setSendingReport(partner.id);
    try {
      const token = getToken();
      await axios.post(`${API}/admin/partners/${partner.id}/send-report`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Relatorio enviado para ${partner.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar relatorio');
    } finally {
      setSendingReport(null);
    }
  };

  const formatCnpj = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 14);
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const openEditModal = (partner) => {
    setEditingPartner(partner);
    setEditForm({
      name: partner.name || '',
      email: partner.email || '',
      cnpj: partner.cnpj || '',
      company_type: partner.company_type || 'MEI',
      phone: partner.phone || '',
      commission_rate: String(partner.commission_rate || 20),
      postal_code: partner.postal_code || '',
      address: partner.address || '',
      address_number: partner.address_number || '',
      province: partner.province || '',
      complement: partner.complement || '',
      create_asaas: false,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingPartner) return;
    setSaving(true);
    try {
      const token = getToken();
      const payload = {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        cnpj: editForm.cnpj,
        company_type: editForm.company_type,
        phone: editForm.phone.trim(),
        commission_rate: parseFloat(editForm.commission_rate) || 20,
        postal_code: editForm.postal_code.trim(),
        address: editForm.address.trim(),
        address_number: editForm.address_number.trim(),
        province: editForm.province.trim(),
        complement: editForm.complement.trim(),
        create_asaas: editForm.create_asaas,
      };
      const res = await axios.put(`${API}/admin/partners/${editingPartner.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.asaas_warning) {
        toast.warning(res.data.asaas_warning, { duration: 12000 });
      } else {
        toast.success(editForm.create_asaas ? 'Parceiro atualizado + Subconta Asaas criada!' : 'Parceiro atualizado!');
      }
      setEditingPartner(null);
      fetchPartners();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleRegisterPayment = async () => {
    if (!showPayment || !paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Informe um valor valido');
      return;
    }
    setSavingPayment(true);
    try {
      const token = getToken();
      const endpoint = paymentType === 'debit' ? 'register-payment' : 'register-credit';
      await axios.post(`${API}/admin/partners/${showPayment.id}/${endpoint}`, {
        amount: parseFloat(paymentAmount),
        description: paymentDesc.trim() || (paymentType === 'debit' ? 'Repasse via Pix' : 'Comissão mensal'),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(paymentType === 'debit'
        ? `Pagamento de R$ ${parseFloat(paymentAmount).toFixed(2)} confirmado!`
        : `Comissão de R$ ${parseFloat(paymentAmount).toFixed(2)} lançada a pagar!`
      );
      setShowPayment(null);
      setPaymentAmount('');
      setPaymentDesc('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao registrar'); }
    finally { setSavingPayment(false); }
  };

  const filtered = partners.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalActive = partners.filter(p => p.status === 'active').length;
  const totalClients = partners.reduce((s, p) => s + (p.active_clients || 0), 0);
  const totalCommission = partners.reduce((s, p) => s + (p.monthly_commission || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-partners-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Handshake className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{totalActive}</p>
                <p className="text-xs text-slate-500">Parceiros Ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{totalClients}</p>
                <p className="text-xs text-slate-500">Clientes Ativos (via Parceiros)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">R$ {totalCommission.toFixed(2).replace('.', ',')}</p>
                <p className="text-xs text-slate-500">Recorrencia Mensal (Split)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar parceiro..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="partner-search-input"
          />
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => { setGuidePartner(null); setShowGuide(true); }}
            variant="outline"
            className="gap-2 border-[#C5A059]/40 text-[#0A2647] hover:bg-[#C5A059]/10"
            data-testid="open-generic-guide-btn"
          >
            <BookOpen className="w-4 h-4" /> Ver Guia
          </Button>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-[#0A2647] hover:bg-[#0A2647]/90 text-white gap-2"
            data-testid="new-partner-btn"
          >
            <Plus className="w-4 h-4" /> Novo Parceiro
          </Button>
        </div>
      </div>

      {/* Create Form Modal */}
      {showForm && (
        <Card className="border-2 border-[#C5A059]/30 shadow-lg" data-testid="partner-create-form">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold text-[#0A2647]">Cadastrar Parceiro Recorrente</CardTitle>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">Nome da Empresa *</Label>
                <Input value={form.name} onChange={e => handleNameChange(e.target.value)}
                  placeholder="Ex: TechSoft ERP" className="mt-1 h-9 text-sm" data-testid="partner-name-input" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="contato@empresa.com" className="mt-1 h-9 text-sm" data-testid="partner-email-input" />
              </div>
            </div>

            {/* Slug field - auto-generated, editable */}
            {form.slug && (
              <div data-testid="partner-slug-field">
                <Label className="text-xs font-medium text-slate-600">Link do Parceiro (editavel)</Label>
                <div className="flex items-center gap-0 mt-1 border border-slate-200 rounded-md overflow-hidden">
                  <span className="bg-slate-50 text-xs text-slate-400 px-3 py-2 border-r border-slate-200 whitespace-nowrap select-none">
                    /parceiro/
                  </span>
                  <Input
                    value={form.slug}
                    onChange={e => setForm(f => ({ ...f, slug: generateSlug(e.target.value) }))}
                    className="border-0 h-9 text-sm font-mono rounded-none focus-visible:ring-0"
                    data-testid="partner-slug-input"
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  URL final: <span className="font-mono text-[#C5A059]">{window.location.origin}/parceiro/{form.slug}</span>
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-600">CNPJ *</Label>
                <Input value={form.cnpj} onChange={e => setForm(f => ({ ...f, cnpj: formatCnpj(e.target.value) }))}
                  placeholder="00.000.000/0001-00" className="mt-1 h-9 text-sm font-mono" maxLength={18}
                  data-testid="partner-cnpj-input" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Tipo de Empresa</Label>
                <select value={form.company_type} onChange={e => setForm(f => ({ ...f, company_type: e.target.value }))}
                  className="w-full mt-1 h-9 text-sm border border-slate-200 rounded-md px-3 bg-white"
                  data-testid="partner-type-select">
                  <option value="MEI">MEI</option>
                  <option value="LIMITED">Limitada</option>
                  <option value="INDIVIDUAL">Individual</option>
                  <option value="ASSOCIATION">Associação</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Telefone</Label>
                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="(31) 99999-0000" className="mt-1 h-9 text-sm" data-testid="partner-phone-input" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Comissão (%) *</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="number" value={form.commission_rate}
                    onChange={e => setForm(f => ({ ...f, commission_rate: e.target.value }))}
                    min="1" max="90" step="1" className="h-9 text-sm w-24 font-mono"
                    data-testid="partner-rate-input" />
                  <span className="text-sm text-slate-500">% sobre valor bruto</span>
                </div>
              </div>
            </div>

            {/* Endereco - Required for Asaas subaccount */}
            {!form.wallet_id.trim() && !form.skip_asaas && (
              <div className="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="partner-address-section">
                <p className="text-xs font-semibold text-slate-600">Endereco (obrigatório para criar subconta Asaas)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[11px] font-medium text-slate-500">CEP *</Label>
                    <Input value={form.postal_code} onChange={e => setForm(f => ({ ...f, postal_code: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
                      placeholder="30130000" className="mt-1 h-9 text-sm font-mono" maxLength={8}
                      data-testid="partner-cep-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[11px] font-medium text-slate-500">Rua/Avenida *</Label>
                    <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                      placeholder="Av. Afonso Pena" className="mt-1 h-9 text-sm"
                      data-testid="partner-address-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-medium text-slate-500">Número *</Label>
                    <Input value={form.address_number} onChange={e => setForm(f => ({ ...f, address_number: e.target.value }))}
                      placeholder="1000" className="mt-1 h-9 text-sm"
                      data-testid="partner-number-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-medium text-slate-500">Bairro *</Label>
                    <Input value={form.province} onChange={e => setForm(f => ({ ...f, province: e.target.value }))}
                      placeholder="Centro" className="mt-1 h-9 text-sm"
                      data-testid="partner-province-input" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-medium text-slate-500">Complemento</Label>
                    <Input value={form.complement} onChange={e => setForm(f => ({ ...f, complement: e.target.value }))}
                      placeholder="Sala 302" className="mt-1 h-9 text-sm"
                      data-testid="partner-complement-input" />
                  </div>
                </div>
              </div>
            )}

            {/* Skip Asaas option */}
            {!form.wallet_id.trim() && (
              <label className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors" data-testid="skip-asaas-option">
                <input
                  type="checkbox"
                  checked={form.skip_asaas}
                  onChange={e => setForm(f => ({ ...f, skip_asaas: e.target.checked }))}
                  className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  data-testid="skip-asaas-checkbox"
                />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Cadastrar sem Asaas (configurar depois)</p>
                  <p className="text-[11px] text-amber-600">O parceiro sera cadastrado sem subconta Asaas. Você podera vincular o Wallet ID depois na tabela de parceiros.</p>
                </div>
              </label>
            )}

            {/* Wallet ID - Optional */}
            <div data-testid="partner-wallet-field">
              <Label className="text-xs font-medium text-slate-600">Wallet ID (Asaas) — opcional</Label>
              <Input value={form.wallet_id} onChange={e => setForm(f => ({ ...f, wallet_id: e.target.value }))}
                placeholder="Cole aqui se o parceiro já possui conta Asaas"
                className="mt-1 h-9 text-sm font-mono" data-testid="partner-wallet-input" />
              <p className="text-[11px] text-slate-400 mt-1">
                {form.wallet_id.trim()
                  ? 'O sistema usara este Wallet ID existente (NAO criara subconta nova).'
                  : 'Vazio = o sistema cria uma subconta Asaas automaticamente pelo CNPJ.'}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                <strong>Exemplo:</strong> Se o plano custa R$ 100,00 e a comissão e {form.commission_rate || 20}%,
                o parceiro recebera R$ {((100 * (parseFloat(form.commission_rate) || 20)) / 100).toFixed(2).replace('.', ',')} automaticamente a cada mensalidade via Split Asaas.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="h-9 text-sm">Cancelar</Button>
              <Button onClick={handleCreate} disabled={creating}
                className="h-9 text-sm bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-semibold"
                data-testid="partner-create-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  form.wallet_id.trim() ? 'Criar Parceiro (Wallet Existente)' :
                  form.skip_asaas ? 'Criar Parceiro (Sem Asaas)' :
                  'Criar Parceiro + Subconta Asaas'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Health Alerts for Partners */}
      {!loading && (() => {
        const unhealthy = partners.filter(p => p.health_issues?.length > 0 && p.status === 'active');
        if (unhealthy.length === 0) return null;
        return (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-500 bg-amber-50/50" data-testid="partner-health-banner">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <p className="font-semibold text-sm text-amber-800">{unhealthy.length} parceiro(s) com cadastro incompleto</p>
              </div>
              <div className="space-y-1.5">
                {unhealthy.map(p => (
                  <div key={p.id} className="flex items-start gap-2 text-xs">
                    <span className="font-semibold text-amber-700 shrink-0">{p.name}:</span>
                    <span className="text-amber-600">{p.health_issues.join(' | ')}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Partners Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center">
            <Handshake className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">
              {partners.length === 0 ? 'Nenhum parceiro cadastrado ainda.' : 'Nenhum resultado encontrado.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <IconLegend items={PARTNER_LEGEND} />
          {filtered.map(p => (
            <Card key={p.id} className={`border-0 shadow-sm transition-all ${p.status === 'inactive' ? 'opacity-50' : ''}`}
              data-testid={`partner-card-${p.id}`}>
              <CardContent className="py-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-slate-800 text-sm truncate">{p.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {p.status === 'active' ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{p.email} | CNPJ: {p.cnpj}</p>
                    <p className="text-xs text-slate-400 mt-0.5">Slug: /parceiro/{p.slug}</p>
                    {/* Health issues */}
                    {p.health_issues?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`partner-health-${p.id}`}>
                        {p.health_issues.map(issue => (
                          <span key={issue} className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">{issue}</span>
                        ))}
                      </div>
                    )}
                    {/* Wallet ID indicator */}
                    {p.wallet_id ? (
                      <p className="text-[10px] text-emerald-600 mt-0.5 flex items-center gap-1">
                        <Check className="w-3 h-3" />Asaas: {p.wallet_id.slice(0, 20)}{p.wallet_id.length > 20 ? '...' : ''}
                      </p>
                    ) : (
                      editingWallet === p.id ? (
                        <div className="flex items-center gap-1.5 mt-1" data-testid={`wallet-edit-form-${p.id}`}>
                          <Input
                            value={walletInput}
                            onChange={e => setWalletInput(e.target.value)}
                            placeholder="Cole o Wallet ID do Asaas"
                            className="h-7 text-xs flex-1 max-w-[250px]"
                            data-testid={`wallet-input-${p.id}`}
                          />
                          <button onClick={async () => {
                            if (!walletInput.trim()) { toast.error('Informe o Wallet ID'); return; }
                            try {
                              await axios.put(`${API}/admin/partners/${p.id}`, { wallet_id: walletInput.trim() },
                                { headers: { Authorization: `Bearer ${getToken()}` } });
                              toast.success('Wallet ID vinculado!');
                              setEditingWallet(null);
                              setWalletInput('');
                              fetchPartners();
                            } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
                          }} className="p-1 hover:bg-emerald-50 rounded text-emerald-600" data-testid={`wallet-save-${p.id}`}>
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => { setEditingWallet(null); setWalletInput(''); }}
                            className="p-1 hover:bg-red-50 rounded text-red-500">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => { setEditingWallet(p.id); setWalletInput(''); }}
                          className="text-[10px] text-amber-600 mt-0.5 flex items-center gap-1 hover:underline cursor-pointer"
                          data-testid={`wallet-link-${p.id}`}>
                          <AlertTriangle className="w-3 h-3" />Sem Asaas — Clique para vincular Wallet ID
                        </button>
                      )
                    )}
                  </div>

                  {/* Commission Rate */}
                  <div className="flex items-center gap-2">
                    {editingRate === p.id ? (
                      <div className="flex items-center gap-1">
                        <Input type="number" value={editRateValue}
                          onChange={e => setEditRateValue(e.target.value)}
                          className="w-20 h-8 text-sm font-mono" min="1" max="90" />
                        <span className="text-xs text-slate-500">%</span>
                        <button onClick={() => handleSaveRate(p.id)}
                          className="p-1 hover:bg-emerald-50 rounded text-emerald-600">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingRate(null)}
                          className="p-1 hover:bg-red-50 rounded text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditingRate(p.id); setEditRateValue(String(p.commission_rate || 20)); }}
                        className="flex items-center gap-1 bg-[#C5A059]/10 px-3 py-1.5 rounded-lg hover:bg-[#C5A059]/20 transition"
                        data-testid={`partner-edit-rate-${p.id}`}>
                        <DollarSign className="w-3.5 h-3.5 text-[#C5A059]" />
                        <span className="text-sm font-bold text-[#C5A059]">{p.commission_rate || 20}%</span>
                        <Edit2 className="w-3 h-3 text-[#C5A059]/60" />
                      </button>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-center">
                    <div>
                      <p className="text-lg font-bold text-slate-800">{p.active_clients || 0}</p>
                      <p className="text-[10px] text-slate-400">Clientes Ativos</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-emerald-600">R$ {(p.monthly_commission || 0).toFixed(2).replace('.', ',')}</p>
                      <p className="text-[10px] text-slate-400">Recorrencia/Mes</p>
                    </div>
                    <div title="Saldo devido ao parceiro (créditos - pagamentos)">
                      <p className={`text-lg font-bold ${(p.balance_due || 0) > 0 ? 'text-red-600' : 'text-slate-400'}`} data-testid={`partner-balance-due-${p.id}`}>R$ {(p.balance_due || 0).toFixed(2).replace('.', ',')}</p>
                      <p className="text-[10px] text-slate-400">A Pagar</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => openEditModal(p)}
                      className="p-2 hover:bg-blue-50 rounded-lg text-blue-600" title="Editar Parceiro"
                      data-testid={`partner-edit-btn-${p.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setShowPayment(p); setPaymentAmount(''); setPaymentDesc(''); setPaymentType('debit'); }}
                      className="p-2 hover:bg-emerald-50 rounded-lg text-emerald-600" title="Registrar Lançamento"
                      data-testid={`partner-payment-btn-${p.id}`}>
                      <DollarSign className="w-4 h-4" />
                    </button>
                    <button onClick={() => setShowQR(showQR === p.id ? null : p.id)}
                      className={`p-2 rounded-lg ${showQR === p.id ? 'bg-[#C5A059]/20 text-[#C5A059]' : 'hover:bg-slate-100 text-slate-500'}`} title="QR Code + Link"
                      data-testid={`partner-qr-btn-${p.id}`}>
                      <QrCode className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleSendReport(p)}
                      disabled={sendingReport === p.id}
                      className="p-2 hover:bg-blue-50 rounded-lg text-blue-500" title="Enviar Relatorio Mensal"
                      data-testid={`partner-send-report-${p.id}`}>
                      {sendingReport === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    </button>
                    <button onClick={() => copyLink(p.slug)}
                      className="p-2 hover:bg-slate-100 rounded-lg text-slate-500" title="Copiar Link">
                      <Copy className="w-4 h-4" />
                    </button>
                    <a href={`/parceiro/${p.slug}`} target="_blank" rel="noreferrer"
                      className="p-2 hover:bg-slate-100 rounded-lg text-slate-500" title="Abrir Landing Page">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    {p.panel_token && (
                      <a href={`/parceiro/painel/${p.panel_token}`} target="_blank" rel="noreferrer"
                        className="p-2 hover:bg-[#C5A059]/10 rounded-lg text-[#C5A059]" title="Acessar Painel do Parceiro"
                        data-testid={`partner-open-panel-${p.id}`}>
                        <LayoutDashboard className="w-4 h-4" />
                      </a>
                    )}
                    <button onClick={() => { setGuidePartner({ name: p.name, slug: p.slug, access_token: p.panel_token }); setShowGuide(true); }}
                      className="p-2 hover:bg-amber-50 rounded-lg text-amber-600" title="Ver Guia do Parceiro"
                      data-testid={`partner-quick-guide-${p.id}`}>
                      <BookOpen className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleToggleStatus(p)}
                      className={`p-2 rounded-lg ${p.status === 'active' ? 'hover:bg-red-50 text-red-500' : 'hover:bg-emerald-50 text-emerald-600'}`}
                      title={p.status === 'active' ? 'Desativar' : 'Ativar'}
                      data-testid={`partner-toggle-${p.id}`}>
                      {p.status === 'active' ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {/* QR Code Expandable Panel */}
                {showQR === p.id && (
                  <div className="mt-4 pt-4 border-t border-slate-200" data-testid={`partner-qr-panel-${p.id}`}>
                    <div className="flex flex-col sm:flex-row items-center gap-5">
                      <div className="bg-white p-3 rounded-xl border-2 border-[#C5A059]/20 shadow-inner">
                        <QRCodeCanvas
                          id={`partner-qr-${p.id}`}
                          value={`https://falocomodono.com.br/parceiro/${p.slug}`}
                          size={160}
                          level="H"
                          marginSize={2}
                          fgColor="#0A2647"
                          bgColor="#FFFFFF"
                        />
                      </div>
                      <div className="flex-1 text-center sm:text-left space-y-2">
                        <p className="text-sm font-bold text-[#0A2647]">Link Exclusivo do Parceiro</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono text-slate-700 truncate">
                            https://falocomodono.com.br/parceiro/{p.slug}
                          </div>
                          <button onClick={() => { navigator.clipboard.writeText(`https://falocomodono.com.br/parceiro/${p.slug}`); toast.success('Link copiado!'); }}
                            className="p-2 bg-[#0A2647] text-white rounded-lg hover:bg-[#0A2647]/90" data-testid={`partner-copy-full-${p.id}`}>
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-slate-500">Envie este link e QR Code para o parceiro. Todas as vendas feitas por este link geram {p.commission_rate || 20}% de comissão recorrente automática via Split Asaas.</p>
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => {
                            const canvas = document.getElementById(`partner-qr-${p.id}`);
                            if (!canvas) return;
                            const hiRes = document.createElement('canvas');
                            hiRes.width = 1024; hiRes.height = 1024;
                            const ctx = hiRes.getContext('2d');
                            ctx.imageSmoothingEnabled = false;
                            ctx.drawImage(canvas, 0, 0, 1024, 1024);
                            const link = document.createElement('a');
                            link.download = `qrcode-parceiro-${p.slug}.png`;
                            link.href = hiRes.toDataURL('image/png');
                            link.click();
                            toast.success('QR Code baixado!');
                          }} className="flex items-center gap-1.5 px-3 py-2 bg-[#C5A059] hover:bg-[#b8933f] text-white rounded-lg text-xs font-semibold"
                            data-testid={`partner-download-qr-${p.id}`}>
                            <Download className="w-3.5 h-3.5" />Baixar QR Code (PNG)
                          </button>
                          <button onClick={() => {
                            const msg = encodeURIComponent(
                              `Ola! Aqui esta seu link exclusivo de parceiro do Falo Com O Dono:\n\nhttps://falocomodono.com.br/parceiro/${p.slug}\n\nTodas as vendas feitas por este link geram comissão recorrente automática para você.`
                            );
                            window.open(`https://wa.me/${(p.phone || '').replace(/\D/g, '')}?text=${msg}`, '_blank');
                          }} className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold"
                            data-testid={`partner-send-whatsapp-${p.id}`}>
                            <ExternalLink className="w-3.5 h-3.5" />Enviar por WhatsApp
                          </button>
                        </div>
                        {/* Partner Panel Link */}
                        {p.panel_token && (
                          <div className="mt-3 p-3 bg-[#0A2647]/5 border border-[#0A2647]/15 rounded-lg" data-testid={`partner-panel-link-${p.id}`}>
                            <p className="text-xs font-bold text-[#0A2647] mb-1.5 flex items-center gap-1.5">
                              <ExternalLink className="w-3.5 h-3.5" />Link do Painel do Parceiro
                            </p>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-[11px] font-mono text-slate-700 truncate">
                                {window.location.origin}/parceiro/painel/{p.panel_token}
                              </div>
                              <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/parceiro/painel/${p.panel_token}`); toast.success('Link do painel copiado!'); }}
                                className="p-2 bg-[#0A2647] text-white rounded-lg hover:bg-[#0A2647]/90" data-testid={`partner-copy-panel-${p.id}`}>
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button onClick={() => {
                                const msg = encodeURIComponent(
                                  `Ola ${p.name}! Aqui esta o link exclusivo do seu Painel de Parceiro do Falo Com O Dono:\n\n${window.location.origin}/parceiro/painel/${p.panel_token}\n\nNele você acompanha seus clientes, comissoes e recorrencia em tempo real.`
                                );
                                window.open(`https://wa.me/${(p.phone || '').replace(/\D/g, '')}?text=${msg}`, '_blank');
                              }} className="flex items-center gap-1.5 px-3 py-2 bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-lg text-xs font-semibold"
                                data-testid={`partner-send-panel-whatsapp-${p.id}`}>
                                <ExternalLink className="w-3.5 h-3.5" />Enviar Painel por WhatsApp
                              </button>
                              <a href={`/parceiro/painel/${p.panel_token}`} target="_blank" rel="noreferrer"
                                className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold">
                                <ExternalLink className="w-3.5 h-3.5" />Visualizar Painel
                              </a>
                              <button onClick={() => { setGuidePartner({ name: p.name, slug: p.slug, access_token: p.panel_token }); setShowGuide(true); }}
                                className="flex items-center gap-1.5 px-3 py-2 bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] rounded-lg text-xs font-semibold"
                                data-testid={`partner-open-guide-${p.id}`}>
                                <BookOpen className="w-3.5 h-3.5" />Ver Guia
                              </button>
                            </div>
                          </div>
                        )}
                        {/* Asaas Withdrawal Instructions */}
                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`partner-asaas-info-${p.id}`}>
                          <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-semibold text-blue-800 mb-1">Como o parceiro recebe o dinheiro?</p>
                              <p className="text-[11px] text-blue-700 leading-relaxed">
                                Ao criar a subconta Asaas, o parceiro recebe um <strong>e-mail automático da Asaas</strong> para definir
                                sua senha e ativar o acesso. Com o acesso ativado, ele pode entrar em{' '}
                                <strong>www.asaas.com</strong>, visualizar os splits recebidos e solicitar{' '}
                                <strong>transferencia/saque diretamente para sua conta bancaria</strong>.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Partner Modal */}
      {editingPartner && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="partner-edit-modal">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto border-2 border-[#C5A059]/30 shadow-2xl">
            <CardHeader className="pb-4 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-bold text-[#0A2647] flex items-center gap-2">
                  <Pencil className="w-4 h-4" /> Editar Parceiro: {editingPartner.name}
                </CardTitle>
                <button onClick={() => setEditingPartner(null)} className="p-1 hover:bg-slate-100 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-slate-600">Nome da Empresa</Label>
                  <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                    className="mt-1 h-9 text-sm" data-testid="edit-partner-name" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600">Email</Label>
                  <Input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                    className="mt-1 h-9 text-sm" data-testid="edit-partner-email" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-slate-600">CNPJ</Label>
                  <Input value={editForm.cnpj} onChange={e => setEditForm(f => ({ ...f, cnpj: formatCnpj(e.target.value) }))}
                    className="mt-1 h-9 text-sm font-mono" maxLength={18} data-testid="edit-partner-cnpj" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600">Tipo de Empresa</Label>
                  <select value={editForm.company_type} onChange={e => setEditForm(f => ({ ...f, company_type: e.target.value }))}
                    className="w-full mt-1 h-9 text-sm border border-slate-200 rounded-md px-3 bg-white"
                    data-testid="edit-partner-type">
                    <option value="MEI">MEI</option>
                    <option value="LIMITED">Limitada</option>
                    <option value="INDIVIDUAL">Individual</option>
                    <option value="ASSOCIATION">Associação</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600">Telefone</Label>
                  <Input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                    className="mt-1 h-9 text-sm" data-testid="edit-partner-phone" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-600">Comissão (%)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input type="number" value={editForm.commission_rate}
                      onChange={e => setEditForm(f => ({ ...f, commission_rate: e.target.value }))}
                      min="1" max="90" step="1" className="h-9 text-sm w-24 font-mono" data-testid="edit-partner-rate" />
                    <span className="text-sm text-slate-500">% sobre valor bruto</span>
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-xs font-semibold text-slate-600">Endereco {!editingPartner.wallet_id ? '(necessário para criar subconta Asaas)' : ''}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[11px] font-medium text-slate-500">CEP</Label>
                    <Input value={editForm.postal_code} onChange={e => setEditForm(f => ({ ...f, postal_code: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
                      placeholder="30130000" className="mt-1 h-9 text-sm font-mono" maxLength={8} data-testid="edit-partner-cep" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[11px] font-medium text-slate-500">Rua/Avenida</Label>
                    <Input value={editForm.address} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                      placeholder="Av. Afonso Pena" className="mt-1 h-9 text-sm" data-testid="edit-partner-address" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-medium text-slate-500">Número</Label>
                    <Input value={editForm.address_number} onChange={e => setEditForm(f => ({ ...f, address_number: e.target.value }))}
                      placeholder="1000" className="mt-1 h-9 text-sm" data-testid="edit-partner-number" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-medium text-slate-500">Bairro</Label>
                    <Input value={editForm.province} onChange={e => setEditForm(f => ({ ...f, province: e.target.value }))}
                      placeholder="Centro" className="mt-1 h-9 text-sm" data-testid="edit-partner-province" />
                  </div>
                  <div>
                    <Label className="text-[11px] font-medium text-slate-500">Complemento</Label>
                    <Input value={editForm.complement} onChange={e => setEditForm(f => ({ ...f, complement: e.target.value }))}
                      placeholder="Sala 302" className="mt-1 h-9 text-sm" data-testid="edit-partner-complement" />
                  </div>
                </div>
              </div>

              {/* Auto-create Asaas option - only when partner has no wallet */}
              {!editingPartner.wallet_id && (
                <label className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors" data-testid="edit-create-asaas-option">
                  <input
                    type="checkbox"
                    checked={editForm.create_asaas}
                    onChange={e => setEditForm(f => ({ ...f, create_asaas: e.target.checked }))}
                    className="w-4 h-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    data-testid="edit-create-asaas-checkbox"
                  />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Criar Subconta Asaas agora</p>
                    <p className="text-[11px] text-emerald-600">Preencha os campos de endereco acima e marque esta opcao para criar a subconta Asaas automaticamente ao salvar.</p>
                  </div>
                </label>
              )}

              {editingPartner.wallet_id && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-600" />
                    <p className="text-xs font-semibold text-emerald-700">Subconta Asaas ativa: {editingPartner.wallet_id}</p>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingPartner(null)} className="h-9 text-sm">Cancelar</Button>
                <Button onClick={handleSaveEdit} disabled={saving}
                  className="h-9 text-sm bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-semibold"
                  data-testid="edit-partner-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> :
                    editForm.create_asaas ? 'Salvar + Criar Subconta Asaas' : 'Salvar Alteracoes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Payment Registration Modal */}
      {showPayment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="partner-payment-modal">
          <Card className="w-full max-w-md border-2 border-emerald-200 shadow-2xl">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-bold text-[#0A2647] flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Movimentação Financeira — {showPayment.name}
                </CardTitle>
                <button onClick={() => setShowPayment(null)} className="p-1 hover:bg-slate-100 rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <button onClick={() => setPaymentType('credit')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${paymentType === 'credit' ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-500'}`}
                  data-testid="payment-type-credit">
                  Lançar Comissão a Pagar
                </button>
                <button onClick={() => setPaymentType('debit')}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${paymentType === 'debit' ? 'bg-red-700 text-white' : 'bg-slate-100 text-slate-500'}`}
                  data-testid="payment-type-debit">
                  Confirmar Pagamento Efetuado
                </button>
              </div>
              {/* Dica contextual */}
              <p className="text-[11px] text-slate-500 leading-snug -mt-2">
                {paymentType === 'credit'
                  ? '🔴 Você deve essa comissão ao parceiro. Use quando uma venda gerar comissão a pagar.'
                  : '🔴 Você já pagou (Pix/transferência). Use para baixar o saldo devido ao parceiro.'}
              </p>
              <div>
                <Label className="text-xs font-medium text-slate-600">Valor (R$)</Label>
                <Input type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)}
                  placeholder="0.00" min="0.01" step="0.01" className="mt-1 h-10 text-lg font-mono font-bold"
                  data-testid="payment-amount" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Descrição (opcional)</Label>
                <Input value={paymentDesc} onChange={e => setPaymentDesc(e.target.value)}
                  placeholder={paymentType === 'debit' ? 'Pix repasse Abril/2026 — quitação 3 vendas' : 'Comissão venda Padaria João — Empresarial — Abr/2026'}
                  className="mt-1 h-9 text-sm" data-testid="payment-desc" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowPayment(null)} className="h-9 text-sm">Cancelar</Button>
                <Button onClick={handleRegisterPayment} disabled={savingPayment}
                  className={`h-9 text-sm font-semibold ${paymentType === 'debit' ? 'bg-red-700 hover:bg-red-800 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}`}
                  data-testid="payment-submit">
                  {savingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> :
                    paymentType === 'debit' ? 'Confirmar Pagamento' : 'Lançar a Pagar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* GUIDE MODAL */}
      {showGuide && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto print:bg-white print:overflow-visible">
          <div className="min-h-full p-4 sm:p-6 print:p-0">
            <div className="max-w-5xl mx-auto bg-slate-50 rounded-2xl shadow-2xl print:shadow-none print:rounded-none print:bg-white">
              <div className="sticky top-0 z-10 flex items-center justify-between bg-white border-b border-slate-200 px-5 py-3 rounded-t-2xl print:hidden">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#C5A059]" />
                  <p className="text-sm font-bold text-[#0A2647]">
                    {guidePartner ? `Guia de ${guidePartner.name}` : 'Guia genérico (mostre para futuros parceiros)'}
                  </p>
                </div>
                <button
                  onClick={() => { setShowGuide(false); setGuidePartner(null); }}
                  className="p-1.5 hover:bg-slate-100 rounded-lg"
                  data-testid="close-guide-btn"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>
              <div className="p-4 sm:p-6 print:p-0">
                <PartnerGuide
                  partnerName={guidePartner?.name}
                  partnerSlug={guidePartner?.slug}
                  partnerToken={guidePartner?.access_token || guidePartner?.token}
                  isGeneric={!guidePartner}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPartnersTab;
